"use client"

import { useState } from "react"
import "./InstructionsPage.css"

const InstructionsPage = () => {
  const [activeSection, setActiveSection] = useState("getting-started")

  const sections = [
    { id: "getting-started", title: "🚀 Getting Started", icon: "🚀" },
    { id: "planets", title: "🌍 Planet System", icon: "🌍" },
    { id: "colony", title: "🏘️ Colony Management", icon: "🏘️" },
    { id: "friends", title: "👥 Social Features", icon: "👥" },
    { id: "laws", title: "⚖️ Law Creation", icon: "⚖️" },
    { id: "evolution", title: "🧬 Evolution Lab", icon: "🧬" },
    { id: "tips", title: "💡 Tips for Success", icon: "💡" },
  ]

  return (
    <div className="instructions-page">
      <div className="instructions-header">
        <h2 className="section-title">
          <span className="title-icon">📖</span>
          How to Play bioForge
        </h2>
        <p className="section-subtitle">Master the art of evolution and survival across hostile worlds</p>
      </div>

      <div className="instructions-layout">
        <div className="instructions-sidebar">
          <nav className="instructions-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`nav-item ${activeSection === section.id ? "active" : ""}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                <span className="nav-title">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="instructions-content">
          {activeSection === "getting-started" && (
            <div className="instruction-section">
              <h3 className="instruction-title">🚀 Getting Started</h3>
              <div className="steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Choose Your Planet</h4>
                    <p>Start by selecting Mars, then unlock more challenging worlds as you progress.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Manage Resources</h4>
                    <p>Balance energy, materials, food, water, oxygen, and knowledge to keep your colony alive.</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Guide Evolution</h4>
                    <p>Help humanity adapt to harsh environments through genetic modifications.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "planets" && (
            <div className="instruction-section">
              <h3 className="instruction-title">🌍 Planet System</h3>
              <div className="planet-guide">
                <div className="planet-item">
                  <div className="planet-icon">🔴</div>
                  <div className="planet-info">
                    <h4>Mars - MEDIUM</h4>
                    <p>Your starting world. Radiation storms and dust devils await.</p>
                    <p>
                      <strong>Unlock:</strong> Available from start
                    </p>
                  </div>
                </div>
                <div className="planet-item">
                  <div className="planet-icon">🌙</div>
                  <div className="planet-info">
                    <h4>Europa - EXTREME</h4>
                    <p>Jupiter's ice moon with deadly radiation and hidden oceans.</p>
                    <p>
                      <strong>Unlock:</strong> Level 5
                    </p>
                  </div>
                </div>
                <div className="planet-item">
                  <div className="planet-icon">🟤</div>
                  <div className="planet-info">
                    <h4>Titan - HARD</h4>
                    <p>Saturn's moon with methane lakes and hydrocarbon rain.</p>
                    <p>
                      <strong>Unlock:</strong> Level 10
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "colony" && (
            <div className="instruction-section">
              <h3 className="instruction-title">🏘️ Colony Management</h3>
              <div className="instruction-block">
                <h4>Resource Management:</h4>
                <div className="resources-guide">
                  <div className="resource-item">
                    <strong>⚡ Energy:</strong> Powers all colony operations and life support
                  </div>
                  <div className="resource-item">
                    <strong>🔧 Materials:</strong> Construction and manufacturing components
                  </div>
                  <div className="resource-item">
                    <strong>🍎 Food:</strong> Sustains your population and enables growth
                  </div>
                  <div className="resource-item">
                    <strong>💧 Water:</strong> Essential for life and industrial processes
                  </div>
                  <div className="resource-item">
                    <strong>💨 Oxygen:</strong> Breathable atmosphere for colonists
                  </div>
                  <div className="resource-item">
                    <strong>🧠 Knowledge:</strong> Research points for technological advancement
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "friends" && (
            <div className="instruction-section">
              <h3 className="instruction-title">👥 Social Features</h3>
              <div className="instruction-block">
                <h4>Colony Raiding System:</h4>
                <p>Unlike traditional combat games, bioForge uses a resource-based raiding system.</p>
                <ul className="instruction-list">
                  <li>
                    <strong>24-Hour Cooldown:</strong> You can only raid each friend once per day
                  </li>
                  <li>
                    <strong>Success Rate:</strong> Based on your level vs their defense rating
                  </li>
                  <li>
                    <strong>Resource Theft:</strong> Steal 10-30% of their available resources
                  </li>
                  <li>
                    <strong>No Damage:</strong> Buildings and population are never harmed
                  </li>
                </ul>
              </div>
            </div>
          )}

          {activeSection === "laws" && (
            <div className="instruction-section">
              <h3 className="instruction-title">⚖️ Law Creation</h3>
              <div className="instruction-block">
                <p>
                  <strong>Unlock Level:</strong> Level 3
                </p>
                <ul className="instruction-list">
                  <li>Create custom laws using AI assistance</li>
                  <li>Laws affect your colony's development</li>
                  <li>Choose from categories like economy, healthcare, space</li>
                  <li>AI generates realistic positive and negative effects</li>
                </ul>
              </div>
            </div>
          )}

          {activeSection === "evolution" && (
            <div className="instruction-section">
              <h3 className="instruction-title">🧬 Evolution Lab</h3>
              <div className="instruction-block">
                <p>
                  <strong>Unlock Level:</strong> Level 10
                </p>
                <ul className="instruction-list">
                  <li>Spend evolution points to enhance your colonists</li>
                  <li>Allocate trait points across intelligence, strength, adaptability, and longevity</li>
                  <li>Each evolution improves your colony's capabilities</li>
                  <li>Higher traits unlock new technologies and survival advantages</li>
                </ul>
              </div>
            </div>
          )}

          {activeSection === "tips" && (
            <div className="instruction-section">
              <h3 className="instruction-title">💡 Tips for Success</h3>
              <div className="tips-list">
                <ul className="instruction-list">
                  <li>Start with Mars to learn the basics before tackling harder planets</li>
                  <li>Balance resource production and consumption carefully</li>
                  <li>Build defenses early to protect against raids</li>
                  <li>Create laws that complement your colony's needs</li>
                  <li>Monitor real-time events for opportunities and threats</li>
                  <li>Add active friends for better raiding opportunities</li>
                  <li>Focus on evolution points to unlock new content faster</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default InstructionsPage
